import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSocket } from '../../context/SocketContext';
import { nativeStandbyPermission, nativeStartupPermission, rememberNativeStartupResponse, rememberDeviceAdminResponse, useDeviceAdminResponses } from './nativeStandbyPermission';

export function powerPolicyValue(item) {
  if (item?.supported === false || item?.status === 'unsupported') return 'Unsupported';
  if (item?.value == null) return 'Not readable';
  if (item.key === 'screen_off_timeout') return `${Math.round(item.value / 60000)} minutes`;
  if (item.key === 'stay_awake') return item.value === 7 ? 'All power sources' : item.value === 0 ? 'Off' : `Mode ${item.value}`;
  if (['native_standby_admin', 'native_startup_screen_on'].includes(item.key)) return item.value === 1 ? 'Active' : 'Not active';
  if (item.key === 'battery_optimization') return item.value === 1 ? 'Unrestricted' : 'Restricted';
  if (['screensaver_enabled', 'screensaver_on_sleep', 'dont_keep_activities', 'low_power'].includes(item.key)) {
    return item.value === 1 ? 'On' : 'Off';
  }
  if (item.key === 'background_process_limit') return item.value === 0 ? 'Standard limit' : String(item.value);
  if (item.key === 'wifi_sleep_policy') return item.value === 2 ? 'Never' : item.value === 1 ? 'Never while plugged in' : 'Default';
  return String(item.value);
}

export default function AndroidPowerPolicyDiagnostics({ areaId, screenId, devices }) {
  const socket = useSocket();
  useDeviceAdminResponses();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [deviceResponse, setDeviceResponse] = useState(null);
  const [liveDevices, setLiveDevices] = useState({});
  const pending = useRef(null);
  const finish = useCallback((payload) => {
    const request = pending.current;
    if (!request || payload.type !== request.responseType
      || Number(payload.deviceId) !== Number(request.deviceId) || payload.requestId !== request.requestId) return;
    clearTimeout(request.timer);
    pending.current = null;
    setBusy(false);
    setDeviceResponse(payload);
    if (request.page === 'refresh') {
      const powerPolicy = payload.powerPolicy || payload.state?.externalApp?.powerPolicy;
      if (powerPolicy) setLiveDevices((current) => ({ ...current, [request.deviceId]: { powerPolicy, bridgeCapabilities: payload.bridgeCapabilities } }));
      setMessage(powerPolicy ? 'Android power settings refreshed from the device.' : 'The device did not report power settings. Update the Suitestream Android app to report these permissions.');
    } else if (request.page === 'turn_screen_on') {
      rememberNativeStartupResponse(request.deviceId, payload);
      setMessage(payload.success === true
        ? payload.fallback === true
          ? 'Android accepted opening a broader settings page. On the TV go to Apps → Special app access → Turn Screen On → Suitestream. No permission change is confirmed.'
          : 'Android accepted the Turn Screen On settings launch. Check the TV and enable Suitestream; a visible page and permission change are not confirmed.'
        : `Turn Screen On settings could not be opened: ${payload.error || payload.message || 'unknown error'}.`);
    } else {
      rememberDeviceAdminResponse(request.deviceId, payload);
      setMessage(payload.success === true && payload.alreadyActive === true
        ? 'Native standby permission is already active.'
        : payload.success === true && (payload.launchAccepted === true || payload.promptOpened === true)
          ? 'Android accepted the permission settings launch. Check the TV for the consent screen; its visibility and activation are not confirmed.'
          : payload.success === false && (payload.supported === false || payload.promptOpened === false)
            ? 'The device could not open the permission prompt. Native standby is unavailable or unsupported on this firmware.'
            : `The device did not confirm a permission settings launch: ${payload.error || payload.message || 'unknown result'}.`);
    }
  }, []);
  useEffect(() => {
    const updateTelemetry = (payload) => {
      const powerPolicy = payload.state?.externalApp?.powerPolicy || payload.state?.powerPolicy;
      if (powerPolicy) setLiveDevices((current) => ({ ...current, [payload.deviceId]: { powerPolicy } }));
    };
    socket?.on('deviceStateUpdate', updateTelemetry);
    socket?.on('deviceCommandResponse', finish);
    return () => {
      socket?.off('deviceCommandResponse', finish);
      socket?.off('deviceStateUpdate', updateTelemetry);
    };
  }, [socket, finish]);
  useEffect(() => () => {
    clearTimeout(pending.current?.timer);
    pending.current = null;
  }, []);
  const requestPermission = async (deviceId, page = 'device_admin') => {
    if (pending.current) return;
    const requestId = `device-admin-${deviceId}-${page}-${Date.now()}`;
    const request = { deviceId, requestId, page, responseType: page === 'refresh' ? 'state_response' : page === 'turn_screen_on' ? 'open_settings_response' : 'request_device_admin_response' };
    pending.current = request;
    setBusy(true);
    setDeviceResponse(null);
    setMessage('Sending permission request; waiting for the device response…');
    request.timer = setTimeout(() => {
      if (pending.current !== request) return;
      pending.current = null;
      setBusy(false);
      setMessage('No device response received. The permission prompt has not been confirmed.');
    }, 10000);
    try {
      if (page === 'turn_screen_on' || page === 'refresh') {
        if (!socket || socket.connected === false) throw new Error('The dashboard connection is unavailable. Reconnect before requesting settings.');
        socket.emit('sendCommand', { deviceId, command: page === 'refresh' ? 'get_state' : 'open_settings', value: page === 'refresh' ? null : { page }, id: requestId });
        return;
      }
      const token = localStorage.getItem('token');
      const base = screenId ? `/api/areas/${areaId}/topology/screens/${screenId}` : `/api/areas/${areaId}`;
      const response = await axios.post(
        `${base}/devices/${deviceId}/request-device-admin`,
        { requestId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.type === request.responseType) {
        finish({ ...response.data, deviceId: response.data.deviceId ?? deviceId, requestId: response.data.requestId ?? requestId });
      } else if (pending.current === request && response.data?.success === false) {
        clearTimeout(request.timer);
        pending.current = null;
        setBusy(false);
        setDeviceResponse(response.data);
        setMessage(response.data.message || 'Permission request could not be delivered.');
      }
    } catch (error) {
      if (pending.current !== request) return;
      clearTimeout(request.timer);
      pending.current = null;
      setBusy(false);
      setMessage(error.response?.data?.message || error.message);
    }
  };

  return <section className="device-lifecycle" aria-label="Android power policy diagnostics">
    <p>Settings launch responses do not confirm a visible prompt or permission grant. Check the TV, then refresh device telemetry.</p>
    {devices.map((source) => {
      const device = { ...source, ...liveDevices[source.id] };
      const permissions = {
        native_standby_admin: { state: nativeStandbyPermission(device), label: 'Native standby', page: 'device_admin',
          recommendation: 'Settings → Device Preferences → Security & restrictions → Device administrators → Suitestream → Activate (where supported).' },
        native_startup_screen_on: { state: nativeStartupPermission(device), label: 'Native startup · Turn Screen On', page: 'turn_screen_on',
          recommendation: 'Settings → Apps → Special app access → Turn Screen On → Suitestream. Enable access on the device.' },
      };
      const items = [...(device.powerPolicy?.items || [])];
      Object.entries(permissions).forEach(([key, entry]) => {
        if (!items.some((item) => item.key === key)) items.push({ key, label: entry.label, value: null });
      });
      return <article className="device-lifecycle__card" key={device.id}>
        <header><div><strong>{device.name}</strong><small>{device.role}</small></div><button type="button" disabled={busy} onClick={() => requestPermission(device.id, 'refresh')}>Refresh settings</button></header>
        {!device.powerPolicy?.items?.length && <p>No Android power policy telemetry available for this device.</p>}
        {items.map((item) => {
          const permission = permissions[item.key];
          const color = permission ? permission.state === 'enabled' ? 'green' : 'unsupported' : item.status;
          return <div key={item.key} className={`device-lifecycle__policy-item ${color}`}>
            <span><strong>{item.label}</strong><small>{permission?.recommendation || item.recommendation}</small></span>
            <em>{permission ? permission.state === 'enabled' ? 'Active' : permission.state === 'disabled' ? 'Unavailable · permission needed'
              : permission.state === 'unsupported' ? 'Unsupported' : 'Unavailable · not reported' : powerPolicyValue(item)}</em>
            {permission && <button type="button" disabled={busy}
              onClick={() => requestPermission(device.id, permission.page)}>{permission.page === 'turn_screen_on' ? 'Open Turn Screen On settings' : 'Open permission prompt'}</button>}
          </div>;
        })}
      </article>;
    })}
    {!devices.length && <p>No devices available in this area.</p>}
    {message && <p role="status">{message}</p>}
    {deviceResponse && <pre aria-label="Device response">{JSON.stringify(deviceResponse, null, 2)}</pre>}
  </section>;
}
